import React from 'react';
import ExpandableSection from '@amzn/awsui-components-console/expandable-section';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import AttributeManagementHelpPanel from 'src/components/AttributeManagement/AttributeManagementHelpPanel';

export interface HelpLink {
	href: string;
	text: string;
}

export interface HelpContent {
	title: string;
	content: React.ReactNode;
	links: HelpLink[];
}

export interface NavigationItem {
	key: string;
	label: string;
}

export const navigationItems: NavigationItem[] = [
	{ key: 'overview', label: 'Overview' },
	{ key: 'workflows', label: 'Manage Workflows' },
	{ key: 'executeWorkflows', label: 'Execute Workflows' },
	{ key: 'search', label: 'Search' },
	{ key: 'serviceRegistry', label: 'Service Registry' },
	{ key: 'attributeManagement', label: 'Attribute Management' },

	{ key: 'troubleshooting', label: 'Troubleshooting' },
];

export const helpContent: Record<string, HelpContent> = {
	overview: {
		title: 'SNOW Overview',
		content: (
			<SpaceBetween size="s">
				<Box>
					SNOW (Simple Node Ordering Workflow) automates the orchestration and configuration of services required for launching, reconfiguring or deprecating Amazon nodes.
				</Box>
				<ExpandableSection headerText="Key Capabilities" defaultExpanded>
					<ul>
						<li><Box variant="strong">Workflow Design:</Box> Drag-and-drop UI to build custom workflows with auto-generated requirement portals</li>
						<li><Box variant="strong">Conditional Execution:</Box> Dynamic execution paths based on attribute values</li>
						<li><Box variant="strong">Task Types:</Box> Generic tasks (Coral API, Lambda, API Gateway) along with specialized tasks for node ecosystem</li>
						<li><Box variant="strong">Bulk Upload:</Box> Launch workflows for up to 300 nodes at once</li>
						<li><Box variant="strong">Real-time Dashboard:</Box> Visualize task status across active workflows</li>
						<li><Box variant="strong">Approval Gating:</Box> Pause execution until approvals are secured</li>
						<li><Box variant="strong">Permissions:</Box> Permission-based model to manage access to workflow creation and execution</li>
					</ul>
				</ExpandableSection>
				<ExpandableSection headerText="Business Use Cases">
					<ul>
						<li>Node setup and configuration workflows</li>
						<li>Address update workflows</li>
						<li>Reconfiguration and retrofit workflows</li>
						<li>Deprecation workflows</li>
					</ul>
				</ExpandableSection>
			</SpaceBetween>
		),
		links: [
			{ href: '#/home', text: 'Go to Home' },
		],
	},
	executeWorkflows: {
		title: 'Execute Workflows',
		content: (
			<SpaceBetween size="s">
				<Box>
					Execute and monitor your workflows.
				</Box>
				<ExpandableSection headerText="Execution Options">
					<ul>
						<li><Box variant="strong">Single Execution:</Box> Use when executing workflows for a small number of nodes</li>
						<li><Box variant="strong">Bulk Execution:</Box> Upload parameters via CSV to execute workflows for multiple nodes without manual entry for each</li>
					</ul>
				</ExpandableSection>
				<ExpandableSection headerText="How to Execute a Workflow">
					<ol>
						<li><Box variant="strong">Select Workflow:</Box> Choose from workflows you have permissions to execute</li>
						<li><Box variant="strong">Choose Execution Type:</Box> Select single execution or bulk execution</li>
						<li><Box variant="strong">Fill Parameters:</Box> Provide all required input parameters and configuration values</li>
						<li><Box variant="strong">Review Configuration:</Box> Double-check all parameters and settings before submission</li>
						<li><Box variant="strong">Submit & Monitor:</Box> Submit the execution and track progress in real-time</li>
					</ol>
				</ExpandableSection>
				<ExpandableSection headerText="Monitoring & Management">
					<ul>
						<li><Box variant="strong">Real-time Progress:</Box> Track workflow execution status and progress indicators</li>
						<li><Box variant="strong">Execution Logs:</Box> View detailed logs and output from each workflow step</li>
						<li><Box variant="strong">Error Handling:</Box> See detailed error messages and retry failed workflow steps</li>
					</ul>
				</ExpandableSection>
				<ExpandableSection headerText="Best Practices">
					<ul>
						<li>Always validate input parameters before execution</li>
						<li>Monitor executions closely, especially for critical operations</li>
						<li>Use single execution when running a workflow for the first time</li>
					</ul>
				</ExpandableSection>
			</SpaceBetween>
		),
		links: [
			{ href: '#/executeWorkflows', text: 'Execute Workflows Dashboard' },
			{ href: '#/manageWorkflows', text: 'Manage Workflows' },
		],
	},
	workflows: {
		title: 'Workflow Management',
		content: (
			<SpaceBetween size="s">
				<Box>
					Create, view, and edit your workflows.
				</Box>
				<ExpandableSection headerText="Manage Workflows">
					<Box>
						The Manage Workflows section allows you to oversee all workflows for which you have permissions in one centralized location.
					</Box>
					<ul>
						<li><Box variant="strong">Self-Serve Workflows:</Box> Create custom workflows tailored to your team&apos;s specific operational needs using our intuitive drag-and-drop canvas interface</li>
						<li><Box variant="strong">View Existing Workflows:</Box> Browse through all workflows for which you have edit permissions and view their current tasks</li>
						<li><Box variant="strong">Edit and Update:</Box> Modify existing workflows to adapt to changing requirements or improve efficiency</li>
						<li><Box variant="strong">Copy to New:</Box> Duplicate existing workflows as starting points (coming soon)</li>
					</ul>
				</ExpandableSection>
				<ExpandableSection headerText="Creating a Workflow">
					<Box>
						Follow these steps to create a new workflow from start to finish.
					</Box>
					<ol>
						<li><Box variant="strong">Start Creation:</Box> Navigate to &quot;Manage Workflows&quot; and click &quot;Create Workflow&quot;</li>
						<li><Box variant="strong">Define Requirements:</Box> Set workflow name, description, and select relevant domains</li>
						<li><Box variant="strong">Design Canvas:</Box> Use the visual canvas to drag and drop tasks from the task library</li>
						<li><Box variant="strong">Configure Tasks:</Box> Set up each task with specific parameters, conditions, and connections</li>
						<li><Box variant="strong">Save & Deploy:</Box> Save your workflow and make it available for execution</li>
					</ol>
					<Box variant="strong">Upcoming Features:</Box>
					<ul>
						<li><Box variant="strong">Custom Validations:</Box> Define and attach validations on task attributes (coming soon)</li>
						<li><Box variant="strong">Auto-population:</Box> Define and attach auto-population on task attributes (coming soon)</li>
					</ul>
					<Box variant="strong">Best Practices:</Box>
					<ul>
						<li>Keep workflows modular and reusable across different scenarios</li>
						<li>Use descriptive names and documentation for workflows and tasks</li>
						<li>Always test workflows in development before production use</li>
						<li>Document workflow purpose, parameters, and expected outcomes</li>
					</ul>
				</ExpandableSection>
			</SpaceBetween>
		),
		links: [
			{ href: '#/manageWorkflows', text: 'Manage Workflows' },
			{ href: '#/createWorkflow', text: 'Create New Workflow' },
			{ href: '#/executeWorkflows', text: 'Execute Workflows' },
		],
	},
	search: {
		title: 'Entity Search',
		content: (
			<SpaceBetween size="s">
				<Box>
					Search and view the workflow, entity, or node.
				</Box>
				<ExpandableSection headerText="Search Features" defaultExpanded>
					<ul>
						<li><Box variant="strong">Workflow Filter:</Box> Select a workflow type to view all associated entities</li>
						<li><Box variant="strong">Status Tracking:</Box> View workflow status for each entity</li>
						<li><Box variant="strong">Progress Tracking:</Box> Monitor and track the progress for the entities</li>
						<li><Box variant="strong">Entity Details:</Box> View comprehensive information about each entity</li>
					</ul>
				</ExpandableSection>
				<ExpandableSection headerText="How to Search">
					<ol>
						<li><Box variant="strong">Select Workflow:</Box> Choose a workflow type from the dropdown</li>
						<li><Box variant="strong">Add Filters:</Box> Optionally add filters like entity name or group ID</li>
						<li><Box variant="strong">Access Details:</Box> Click on an entity name to view its execution details</li>
					</ol>
				</ExpandableSection>
			</SpaceBetween>
		),
		links: [
			{ href: '#/entitySearch', text: 'Go to Entity Search' },
		],
	},
	troubleshooting: {
		title: 'Troubleshooting',
		content: (
			<SpaceBetween size="s">
				<Box>
					Common issues and their solutions to help you resolve problems quickly.
				</Box>
				<ExpandableSection headerText="Common Issues" defaultExpanded>
					<ul>
						<li><Box variant="strong">Workflow Execution Fails:</Box> Review the subtask error message and retry. If the issue persists, raise a ticket with the SNOW team</li>
						<li><Box variant="strong">Workflow Not Visible:</Box> Search for the workflow in Manage Workflows page, locate the associated bindle, and contact the owners for access</li>
						<li><Box variant="strong">Timeout Errors:</Box> Check network connectivity and retry</li>
					</ul>
				</ExpandableSection>
				<ExpandableSection headerText="Getting Help">
					<Box>If you need additional assistance:</Box>
					<ul>
						<li>Check the contact page for support information</li>
						<li>Review the documentation links and user guides provided</li>
						<li>Contact the bindle owners</li>
					</ul>
				</ExpandableSection>
			</SpaceBetween>
		),
		links: [
			{ href: '#/contact', text: 'Contact Us' },
		],
	},
	serviceRegistry: {
		title: 'Service Registry',
		content: (
			<SpaceBetween size="s">
				<Box>
					Register and manage applications and operations in the SNOW Service Registry. Applications are containers for operations that enable service integration and workflow automation.
				</Box>
				<ExpandableSection headerText="Prerequisites" defaultExpanded>
					<Box>Before registering a new application, complete these prerequisites:</Box>
					<ul>
						<li><Box variant="strong">Bindle ID:</Box> Obtain your application&apos;s Bindle identifier (format: <code>amzn1.bindle.resource.*</code>)</li>
						<li><Box variant="strong">AAA Relationship:</Box> Establish authentication between SNOW and your service (requires SNOW team ticket, 2-3 business days)</li>
						<li><Box variant="strong">SuperStar Connectivity:</Box> Configure network connectivity (requires SNOW team ticket, can run in parallel)</li>
						<li><Box variant="strong">CTI:</Box> Identify Category, Type, and Item for cost tracking and ticket management</li>
						<li><Box variant="strong">Owner Information:</Box> LDAP/POSIX group responsible for the application</li>
					</ul>
				</ExpandableSection>
				<ExpandableSection headerText="Adding an Application">
					<Box>Register your service using a 3-step wizard:</Box>
					<ol>
						<li><Box variant="strong">Basic Information:</Box> Application name (maps to AAA), description, domain, and sub-domains</li>
						<li><Box variant="strong">Ownership &amp; Classification:</Box> Bindle ID, owner group, and CTI (Category/Type/Item)</li>
						<li><Box variant="strong">Review &amp; Submit:</Box> Verify all information before creating</li>
					</ol>
				</ExpandableSection>
				<ExpandableSection headerText="Adding an Operation">
					<Box>Register an API endpoint or function using a 6-step wizard:</Box>
					<ol>
						<li><Box variant="strong">Basic Information:</Box> Operation name, description, version, idempotent flag</li>
						<li><Box variant="strong">Integration Configuration:</Box> Choose API Gateway or Coral, configure service names</li>
						<li><Box variant="strong">Request Configuration:</Box> HTTP method, endpoints, request body with <code>#attributeId#</code> placeholders</li>
						<li><Box variant="strong">Response Configuration:</Box> Response body, success/failure criteria</li>
						<li><Box variant="strong">Task I/O:</Box> Define task inputs and outputs</li>
						<li><Box variant="strong">Review &amp; Submit:</Box> Verify configuration before creating</li>
					</ol>
				</ExpandableSection>
				<ExpandableSection headerText="Exporting to Task">
					<Box>Export an operation to create a reusable workflow task:</Box>
					<ul>
						<li>Exported tasks appear in the workflow creation interface for drag-and-drop design</li>
						<li>Task name must be unique across all workflows</li>
						<li>Changes to the original operation do NOT update exported tasks — re-export if needed</li>
					</ul>
				</ExpandableSection>
			</SpaceBetween>
		),
		links: [
			{ href: '#/applicationManagement', text: 'Application Management' },
			{ href: 'https://w.amazon.com/bin/view/SNOW/Service_Onboarding/Application_Operation_Onboarding', text: 'Onboarding Guide' },
			{ href: 'https://t.corp.amazon.com/create/templates/46ad46f4-b1d2-47b7-b6cd-8ec5e0d9679e', text: 'Request AAA/SuperStar Setup' },
		],
	},
	attributeManagement: {
		title: 'Attribute Management',
		content: <AttributeManagementHelpPanel />,
		links: [
			{
				href: 'https://w.amazon.com/bin/view/SNOW/UserGuide/Attribute_Management/',
				text: 'Attribute Management User Guide',
			},
		],
	},
};
